import ApexCharts, { ApexOptions } from "apexcharts";
import { FC, useEffect, useRef } from "react";
import { getCSS } from "../../../_metronic/assets/ts/_utils";
import { useIntl } from "react-intl";

export type MonthlyEffectiveness = {
  month: string;
  uniqueOrders: number;
  reviews: number;
  effectivenessRate: number;
};

type Props = {
  data: MonthlyEffectiveness[];
  loading: boolean;
};

const NotificationEffectivenessChart: FC<Props> = ({ data, loading }) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const intl = useIntl();

  const hasData = data.length > 0 && data.some((d) => d.uniqueOrders > 0);

  useEffect(() => {
    if (!chartRef.current) return;
    if (!hasData) return;

    const height = parseInt(getCSS(chartRef.current, "height")) || 250;
    const chart = new ApexCharts(chartRef.current, getChartOptions(height, data, intl));
    chart.render();

    return () => chart.destroy();
  }, [data, hasData, intl]);

  return (
    <div>
      {loading && (
        <div className="text-center py-4">
          <span className="spinner-border spinner-border-sm spinner-border-primary align-middle" />
        </div>
      )}
      {!loading && !hasData && (
        <div className="text-center text-muted py-4">
          {intl.formatMessage({
            id: "page.dashboard.effectiveness-chart.no-data",
            defaultMessage: "Sem dados de efetividade no período",
          })}
        </div>
      )}
      <div ref={chartRef} style={{ height: hasData && !loading ? "250px" : "0px" }} />
    </div>
  );
};

export default NotificationEffectivenessChart;

function getChartOptions(height: number, data: MonthlyEffectiveness[], intl: any): ApexOptions {
  const labels = data.map((d) => {
    const [year, month] = d.month.split("-");
    return `${month}/${year}`;
  });

  return {
    series: [
      {
        name: intl.formatMessage({
          id: "chart-effectiveness-rate",
          defaultMessage: "Taxa de efetividade",
        }),
        data: data.map((d) => parseFloat((d.effectivenessRate * 100).toFixed(2))),
      },
    ],
    chart: {
      type: "line",
      height,
      fontFamily: "inherit",
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    stroke: { curve: "smooth", width: 3 },
    markers: { size: 5 },
    xaxis: {
      categories: labels,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: { style: { fontSize: "12px" } },
    },
    yaxis: {
      min: 0,
      labels: {
        formatter: (val) => `${val.toFixed(1)}%`,
        style: { fontSize: "12px" },
      },
    },
    tooltip: {
      y: {
        formatter: (val, { dataPointIndex }) => {
          const item = data[dataPointIndex];
          return `${val}% — ${item.reviews.toLocaleString("pt-BR")} av. / ${item.uniqueOrders.toLocaleString("pt-BR")} pedidos`;
        },
      },
    },
    grid: {
      strokeDashArray: 4,
      yaxis: { lines: { show: true } },
    },
    dataLabels: { enabled: false },
  };
}
